import { useCallback, useMemo } from "react";
import type { Editor, JSONContent } from "@tiptap/react";
import { serializeTemplate } from "@/parser/templateSerializer";
import type { TemplateMeta } from "@/parser/templateSerializer";
import { documentToContentNodes } from "@/components/unified/documentToContentNodes";
import { documentToReport } from "@/components/unified/documentToReport";
import { useSaveAsHandler, useInsertHandlers } from "@/components/editor/useTemplateActions";
import type { Evaluation, SubTemplateData } from "@/types/evaluation";
import type { Template } from "@/types/template";

interface UseEditorHandlersParams {
  mode: "build" | "fill";
  meta: TemplateMeta;
  setMeta: React.Dispatch<React.SetStateAction<TemplateMeta>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  template: Template | undefined;
  evaluation: Evaluation | undefined;
  templateRemove: (id: string) => Promise<void>;
  evalRemove: (id: string) => Promise<void>;
  evalSave: (data: Evaluation) => Promise<void>;
  debouncedSave: (source: string) => void;
  cancelAutoSave: () => void;
  editorRef: React.RefObject<Editor | null>;
  allHeadings: { text: string; sectionId?: string; isSubTemplate: boolean; level: number }[];
  scrollToHeading: (idx: number) => void;
  values: Record<string, unknown>;
  variableValues: Record<string, string>;
  subTemplateValues: Record<string, SubTemplateData>;
  sectionToggles: Record<string, boolean>;
  fieldToggles: Record<string, boolean>;
  collapsedSections: Record<string, boolean>;
  unifiedDocument: JSONContent | null;
  name: string;
  allTemplates: Template[];
  cloneFromSeed: (seedId: string, editedSource: string) => Promise<Template>;
  interpolate: (s: string) => string;
  toast: (msg: string, type?: "success" | "error" | "info") => void;
  navigate: (path: string) => void | Promise<void>;
  pendingInsertPosRef: React.RefObject<number | null>;
  setShowInsertPicker: React.Dispatch<React.SetStateAction<boolean>>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  setSubTemplateValues: React.Dispatch<React.SetStateAction<Record<string, SubTemplateData>>>;
  setSectionToggles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setFieldToggles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setCollapsedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

function serializeFromEditor(editor: Editor, meta: TemplateMeta): string {
  return serializeTemplate(documentToContentNodes(editor.getJSON()), meta);
}

function useFieldSetters(params: UseEditorHandlersParams) {
  const { setFieldToggles, setSectionToggles, setSubTemplateValues, setValues } = params;

  const setFieldValue = useCallback(
    (fieldId: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [fieldId]: value }));
    },
    [setValues],
  );

  const setSubTemplateValue = useCallback(
    (fieldId: string, data: SubTemplateData) => {
      setSubTemplateValues((prev) => ({ ...prev, [fieldId]: data }));
    },
    [setSubTemplateValues],
  );

  const handleToggleSection = useCallback(
    (sectionId: string) => {
      setSectionToggles((prev) => ({ ...prev, [sectionId]: !(prev[sectionId] ?? true) }));
    },
    [setSectionToggles],
  );

  const handleToggleField = useCallback(
    (fieldId: string) => {
      setFieldToggles((prev) => ({ ...prev, [fieldId]: !(prev[fieldId] ?? true) }));
    },
    [setFieldToggles],
  );

  return { handleToggleField, handleToggleSection, setFieldValue, setSubTemplateValue };
}

function useCollapseHandlers(params: UseEditorHandlersParams) {
  const { allHeadings, collapsedSections, scrollToHeading, setCollapsedSections } = params;

  const handleCollapseSection = useCallback(
    (sectionId: string) => {
      setCollapsedSections((prev) => ({ ...prev, [sectionId]: !(prev[sectionId] ?? false) }));
      const scrollToCollapsed = () => {
        const idx = allHeadings.findIndex((h) => h.sectionId === sectionId);
        if (idx !== -1) {
          scrollToHeading(idx);
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToCollapsed);
      });
    },
    [allHeadings, scrollToHeading, setCollapsedSections],
  );

  const sectionIds = useMemo(
    () =>
      allHeadings
        .filter((h): h is typeof h & { sectionId: string } => h.sectionId !== undefined)
        .map((h) => h.sectionId),
    [allHeadings],
  );

  const allCollapsed = sectionIds.length > 0 && sectionIds.every((id) => collapsedSections[id]);

  const handleCollapseAll = useCallback(() => {
    setCollapsedSections((prev) => {
      const next = { ...prev };
      for (const id of sectionIds) {
        next[id] = true;
      }
      return next;
    });
  }, [sectionIds, setCollapsedSections]);

  const handleExpandAll = useCallback(() => {
    setCollapsedSections((prev) => {
      const next = { ...prev };
      for (const id of sectionIds) {
        next[id] = false;
      }
      return next;
    });
  }, [sectionIds, setCollapsedSections]);

  return { allCollapsed, handleCollapseAll, handleCollapseSection, handleExpandAll, sectionIds };
}

function useMetaChangeHandlers(params: UseEditorHandlersParams) {
  const { debouncedSave, editorRef, meta, mode, setMeta, setName } = params;

  const handleNameChange = (newName: string) => {
    if (mode === "build") {
      const newMeta = { ...meta, name: newName };
      setMeta(newMeta);
      if (editorRef.current) {
        debouncedSave(serializeFromEditor(editorRef.current, newMeta));
      }
    } else {
      setName(newName);
    }
  };

  const handleCategoryChange = (category: string) => {
    const newMeta = { ...meta, category: category || undefined };
    setMeta(newMeta);
    if (editorRef.current) {
      debouncedSave(serializeFromEditor(editorRef.current, newMeta));
    }
  };

  return { handleCategoryChange, handleNameChange };
}

function useDeleteHandler(params: UseEditorHandlersParams) {
  const { evalRemove, evaluation, mode, navigate, template, templateRemove, toast } = params;

  return async () => {
    if (mode === "build") {
      if (!template) {
        return;
      }
      await templateRemove(template.id);
      toast("Template deleted", "success");
      void navigate("/templates");
    } else {
      if (!evaluation) {
        return;
      }
      await evalRemove(evaluation.id);
      toast("Evaluation deleted", "success");
      void navigate("/fill");
    }
  };
}

function buildCompletedEvaluation(params: UseEditorHandlersParams) {
  const {
    evaluation,
    fieldToggles,
    name,
    sectionToggles,
    subTemplateValues,
    unifiedDocument,
    values,
    variableValues,
  } = params;
  if (!evaluation) {
    return;
  }
  return {
    ...evaluation,
    completedAt: evaluation.completedAt ?? Date.now(),
    fieldToggles,
    fillVariables: variableValues,
    name,
    sectionToggles,
    status: "completed" as const,
    subTemplateValues,
    unifiedDocument: unifiedDocument as Record<string, unknown> | undefined,
    values,
  };
}

function useAsyncActionHandlers(params: UseEditorHandlersParams) {
  const {
    cancelAutoSave,
    evalSave,
    evaluation,
    fieldToggles,
    interpolate,
    navigate,
    sectionToggles,
    subTemplateValues,
    toast,
    unifiedDocument,
    values,
  } = params;

  const handleDelete = useDeleteHandler(params);

  const handleCopyReport = async () => {
    if (!unifiedDocument) {
      return;
    }
    const text = documentToReport(
      unifiedDocument,
      values,
      subTemplateValues,
      interpolate,
      sectionToggles,
      fieldToggles,
    );
    try {
      await navigator.clipboard.writeText(text);
      toast("Report copied to clipboard", "success");
    } catch {
      toast("Failed to copy", "error");
    }
  };

  const handleMarkCompleted = async () => {
    if (!evaluation) {
      return;
    }
    cancelAutoSave();
    const completed = buildCompletedEvaluation(params);
    if (completed) {
      await evalSave(completed);
    }
    toast("Evaluation marked as completed", "success");
    void navigate("/fill");
  };

  return { handleCopyReport, handleDelete, handleMarkCompleted };
}

export function useEditorHandlers(params: UseEditorHandlersParams) {
  const fieldSetters = useFieldSetters(params);
  const collapseHandlers = useCollapseHandlers(params);
  const metaHandlers = useMetaChangeHandlers(params);
  const asyncHandlers = useAsyncActionHandlers(params);
  const insertHandlers = useInsertHandlers(params);
  const handleSaveAs = useSaveAsHandler(params);

  return {
    ...fieldSetters,
    ...collapseHandlers,
    ...metaHandlers,
    ...asyncHandlers,
    ...insertHandlers,
    handleSaveAs,
  };
}
